import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, ArrowRight, CreditCard, Plane, Wallet, Lock, BadgeCheck, Gift, Award, Timer } from 'lucide-react';
import Footer from '@/components/Footer';
import PageSeo from '@/components/PageSeo';
import GuideCard from '@/components/guides/GuideCard';
import { CATEGORY_ICONS } from '@/components/guides/categoryIcons';
import { hubSections } from '@/content/guides/catalog';

/** The hand-built guides keep the icons they've always had; data guides use their category's. */
const PAGE_ICONS = {
  'fastest-flight-search-engines': Timer,
  'fintech-installment-flight-booking': CreditCard,
  'flight-installments-bnpl-payment-options': Wallet,
  'flight-layaway-vs-bnpl': BadgeCheck,
  'lock-flight-price-with-deposit': Lock,
  'pay-flights-with-gift-cards-credits': Gift,
  'semi-private-flights-bnpl': Plane,
  'defera-vs-affirm-klarna-uplift': Award,
};

const SECTION_BLURBS = {
  Research: 'Original studies from our own searches: what flight sites really charge, how fast they are, and when fares move.',
  'Pay over time': 'Every way to spread the cost of a flight, with or without credit, and how the options compare.',
  'Booking tips': 'Practical help with timing, fare holds, budgets and booking safely.',
};

const iconFor = (card) => PAGE_ICONS[card.to.replace('/guides/', '')] || CATEGORY_ICONS[card.section] || BookOpen;

export default function Guides() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageSeo path="/guides" />

      <div className="min-h-screen bg-black text-white">
        {/* Navigation Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
          className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/[0.08]"
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="md:hidden">Home</span>
              <span className="hidden md:inline">Back to Home</span>
            </Link>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/how-it-works')}
                className="text-white/30 hover:text-white/60 text-sm font-normal transition-colors"
              >
                How it Works
              </button>
              <button
                onClick={() => navigate('/search')}
                className="bg-white text-black px-4 py-1.5 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
              >
                Search Flights
              </button>
            </div>
          </div>
        </motion.div>

        {/* Hero Section */}
        <section className="pt-28 pb-16 px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <BookOpen className="w-9 h-9 text-white/80 mb-5" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-4 tracking-tight">
                Guides & Research
              </h1>
              <p className="text-lg md:text-xl text-white/60 leading-relaxed max-w-2xl">
                Guides to booking and paying for flights, and original research on how flight sites, fares and pay-later options really work.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Guides, by section */}
        {hubSections().map(({ section, cards }) => (
          <section key={section} className="py-10 px-6 last:pb-20">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl md:text-[28px] font-semibold text-white tracking-tight mb-2">{section}</h2>
              <p className="text-white/55 text-[15px] mb-7 max-w-2xl">{SECTION_BLURBS[section]}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {cards.map((card, index) => (
                  <motion.div
                    key={card.to}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.03 + Math.min(index, 8) * 0.04, ease: [0.22, 0.61, 0.36, 1] }}
                  >
                    <GuideCard {...card} icon={iconFor(card)} />
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        ))}

        {/* CTA Section */}
        <section className="py-20 px-6 border-t border-white/[0.06]">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3 tracking-tight">
                Ready to book your next trip?
              </h2>
              <p className="text-white/65 text-[15px] md:text-base mb-8 max-w-lg mx-auto">
                Apply what you’ve learned and book flights with flexible payment plans today.
              </p>
              <button
                onClick={() => navigate('/search')}
                className="inline-flex items-center gap-2 bg-white hover:bg-white/90 text-black px-7 py-3.5 rounded-full font-semibold text-[15px] transition-colors"
              >
                Search Flights
                <ArrowRight className="w-5 h-5" strokeWidth={2} />
              </button>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
}

